import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DealbreakerQuestion, DealbreakerQuestionInput } from "@/types/dealbreaker";
import { toast } from "sonner";

/**
 * Hook to fetch dealbreaker questions for a specific profile
 */
export function useDealbreakerQuestions(profileId: string | undefined) {
  return useQuery({
    queryKey: ["dealbreaker-questions", profileId],
    queryFn: async () => {
      if (!profileId) return [];

      const { data, error } = await supabase
        .from("dealbreaker_questions")
        .select("*")
        .eq("profile_id", profileId)
        .order("question_order", { ascending: true });

      if (error) throw error;
      return (data || []) as DealbreakerQuestion[];
    },
    enabled: !!profileId,
  });
}

/**
 * Hook to save/update dealbreaker questions for the current user's profile
 */
export function useSaveDealbreakerQuestions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      profileId,
      questions,
      silent = false,
    }: {
      profileId: string;
      questions: DealbreakerQuestionInput[];
      silent?: boolean;
    }) => {
      // First, delete all existing questions for this profile
      const { error: deleteError } = await supabase
        .from("dealbreaker_questions")
        .delete()
        .eq("profile_id", profileId);

      if (deleteError) throw deleteError;

      // If no questions to save, we're done
      if (questions.length === 0) {
        return { data: [] as DealbreakerQuestion[], silent };
      }

      // Insert new questions
      const questionsToInsert = questions.map((q) => ({
        profile_id: profileId,
        question_text: q.question_text.trim(),
        question_order: q.question_order,
      }));

      const { data, error: insertError } = await supabase
        .from("dealbreaker_questions")
        .insert(questionsToInsert)
        .select();

      if (insertError) throw insertError;
      return { data: (data || []) as DealbreakerQuestion[], silent };
    },
    onSuccess: (result, variables) => {
      // Invalidate and refetch questions
      queryClient.invalidateQueries({
        queryKey: ["dealbreaker-questions", variables.profileId],
      });
      // Only show toast if not silent
      if (!result.silent) {
        toast.success("Dealbreaker questions saved successfully");
      }
    },
    onError: (error: any) => {
      console.error("dealbreakers.save_questions_failed");
      toast.error(error.message || "Failed to save dealbreaker questions");
    },
  });
}
